import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CognitoContext,
  UserProviderActions,
} from "../../contexts/cognito/cognito-provider";
import { AppRouteNames } from "../../routes/app-routes";
import { CoreServiceSelector } from "../../services/core/service-selector";
import { consumePrefetch, prefetchUserRecord } from "../../services/core/users-prefetch";

export const Card = ({ children, className = "" }) => (
  <div
    className={[
      "bg-[#0D0E1D] rounded-2xl p-4 sm:p-5",
      "ring-1 ring-[#34353F] shadow-md",
      className,
    ].join(" ")}
  >
    {children}
  </div>
);

export const MenuItem = ({
  iconLeft = null,
  label,
  onClick,
  onMouseEnter,
  right = <ChevronRightIcon className="text-xl" />,
  href,
  target = "_self",
  title,
}) => {
  const Comp = href ? "a" : "button";
  const linkProps = href
    ? {
      href,
      target,
      rel: target === "_blank" ? "noopener noreferrer" : undefined,
    }
    : { type: "button", onClick };

  return (
    <Comp
      {...linkProps}
      onMouseEnter={onMouseEnter}
      className={[
        "w-full px-4 py-3 ",
        "bg-[#0D0E1D] rounded-2xl ring-1 ring-[#34353F]",
        "flex items-center justify-between",
      ].join(" ")}
      title={title || label}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {iconLeft}
        <span className="font-oswald text-base font-medium text-white truncate">
          {label}
        </span>
      </div>
      <div className="shrink-0 ml-3">{right}</div>
    </Comp>
  );
};

export const AccordionItem = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[#14141d] border-[1px] border-solid border-[#20202c] rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full bg-transparent border-0 px-[22px] py-[18px] flex items-center justify-between text-left cursor-pointer"
      >
        <span className="font-body-primary text-[16px] font-semibold text-white">
          {title}
        </span>
        <ExpandMoreIcon
          className="text-xl shrink-0"
          style={{
            transition: "transform 300ms ease-in-out",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>

      <div
        className={[
          "grid transition-all duration-300 ease-out",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        ].join(" ")}
      >
        <div className="overflow-hidden">
          <div className="px-[22px] pb-[18px] pt-[14px] border-t-[1px] border-solid border-white/[0.08] text-[#b6b6c1] text-[14px] leading-[1.55] font-body-primary">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Manage Users helpers ─────────────────────────────────────────────────────
const getUserStatus = (u) =>
  u.LastAccessDate ? "active" : u.IsBlocked ? "blocked" : "invited";
const isUserAdmin = (u) => (u.UserRoles?.split(",") || []).includes("useradmin");
// Active first, then invited/blocked; admins first within each group.
const userRoleRank = (u) => (getUserStatus(u) === "active" ? 0 : 2) + (isUserAdmin(u) ? 0 : 1);

const STATUS_LABEL = { active: "Active", blocked: "Blocked", invited: "Invited" };
const STATUS_BADGE = {
  active: "bg-[#18a957]",
  blocked: "bg-[#6b7280]",
  invited: "bg-[#e23b3b]",
};

const Badge = ({ className, children }) => (
  <span
    className={[
      "text-xs font-bold px-[13px] py-1 rounded-full text-white whitespace-nowrap",
      className,
    ].join(" ")}
  >
    {children}
  </span>
);

const ManageUserCard = ({ user, service }) => {
  const status = getUserStatus(user);
  const admin = isUserAdmin(user);
  return (
    <Link
      to={`/edit-user/${user.Id}`}
      target="_self"
      onMouseEnter={() => service && prefetchUserRecord(service, user.Id)}
      className="no-underline"
    >
      <div id={`user-card-${user.Id}`} className="bg-[#14141d] border-[1px] border-solid border-[#20202c] hover:border-[#2c2c3a] rounded-2xl px-[20px] py-[16px] flex items-start justify-between gap-[14px] transition-colors">
        <div className="min-w-0 flex-1">
          <p className="m-0 mb-[3px] text-base font-bold text-white truncate font-body-primary">
            {user.FullName}
          </p>
          <p className="m-0 mb-[7px] text-[13.5px] text-[#b6b6c1] truncate font-body-primary">
            {user.Email}
          </p>
          <p className="m-0 text-[13.5px] text-[#8a8a96] truncate font-body-primary">
            {user.CompanyName}
          </p>
        </div>
        <div className="flex gap-2 items-center shrink-0">
          {admin && <Badge className="bg-[#2563eb]">Admin</Badge>}
          <Badge className={STATUS_BADGE[status]}>{STATUS_LABEL[status]}</Badge>
        </div>
      </div>
    </Link>
  );
};

const pillBtn = [
  "px-[22px] py-[10px] rounded-full text-white border-[1px] border-solid border-white/65",
  "hover:bg-white/10 active:scale-[0.98] transition",
  "whitespace-nowrap font-body-primary font-semibold text-[15px] shrink-0",
].join(" ");

const ProfileForm = () => {
  const { state, dispatch } = useContext(CognitoContext);
  const navigate = useNavigate();
  const serviceRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const userInfo = state?.userInfo || null;

  const signoutHandler = (event) => {
    event.preventDefault();
    state.cognitoUser.signOut(() => {
      dispatch({ type: UserProviderActions.signout });
      navigate(AppRouteNames?.signin ?? "/signin");
    });
  };

  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      const service = CoreServiceSelector.GetUserManagementService(state.session);
      if (!service) {
        setUsersLoading(false);
        return;
      }
      serviceRef.current = service;
      setUsersLoading(true);
      const prefetched = consumePrefetch();
      const result = await (prefetched ?? service.getUsersList()).catch(() => null);
      if (cancelled) return;
      setUsers(result || []);
      setUsersLoading(false);
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.session]);

  const sortedUsers = [...users].sort((a, b) => userRoleRank(a) - userRoleRank(b));

  if (!userInfo) {
    return (
      <div className="overflow-x-auto text-white">
        <div className="max-w-[980px] min-[880px]:ml-24 px-[12px] pt-[18px] min-[880px]:px-9 min-[880px]:pt-7 space-y-4">
          <div className="h-20 bg-white/5 rounded-2xl border-[1px] border-solid border-[#20202c] animate-pulse" />
          <div className="h-14 bg-white/5 rounded-2xl border-[1px] border-solid border-[#20202c] animate-pulse" />
          <div className="h-14 bg-white/5 rounded-2xl border-[1px] border-solid border-[#20202c] animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto text-white">
      {/* Design `.main`: 96px sidebar offset, left-aligned, max-width 980, 36px padding */}
      <div className="max-w-[980px] min-[880px]:ml-24 px-[12px] pt-[18px] pb-24 min-[880px]:px-9 min-[880px]:pt-7 min-[880px]:pb-[60px]">
        {/* Root title */}
        <h1 className="m-0 !mb-[22px] px-1 text-[26px] font-bold font-headings-primary tracking-[-0.01em] text-white" style={{ lineHeight: "1.15" }}>
          Profile
        </h1>

        <div className="flex flex-col gap-[14px]">
          {/* Account card */}
          <div className="bg-[#14141d] border-[1px] border-solid border-[#20202c] rounded-2xl px-[22px] py-[18px] flex items-center justify-between gap-[16px]">
            <div className="min-w-0 flex-1">
              <h2 className="m-0 mb-1 text-lg font-bold text-white font-body-primary truncate">
                {(userInfo?.FirstName ?? "") + " " + (userInfo?.LastName ?? "")}
              </h2>
              <div
                className="m-0 text-[#b6b6c1] text-sm font-body-primary truncate"
                title={userInfo?.Email}
              >
                {userInfo?.Email}
              </div>
            </div>

            <button type="button" onClick={signoutHandler} className={pillBtn}>
              Log out
            </button>
          </div>

          {/* Manage Users */}
          <div className="flex items-center justify-between gap-[16px] px-[22px] mt-[12px] mb-0.5">
            <h2 className="m-0 text-[19px] font-bold text-white font-body-primary tracking-[-0.01em]">
              Manage Users
            </h2>
            <button
              type="button"
              onClick={() => navigate(AppRouteNames?.addUser ?? "/add-user")}
              className={pillBtn}
            >
              New user
            </button>
          </div>

          {usersLoading ? (
            <div className="flex flex-col gap-[12px]">
              <div className="h-[86px] bg-white/5 rounded-2xl border-[1px] border-solid border-[#20202c] animate-pulse" />
              <div className="h-[86px] bg-white/5 rounded-2xl border-[1px] border-solid border-[#20202c] animate-pulse" />
              <div className="h-[86px] bg-white/5 rounded-2xl border-[1px] border-solid border-[#20202c] animate-pulse" />
            </div>
          ) : sortedUsers.length ? (
            <div className="flex flex-col gap-[12px]">
              {sortedUsers.map((u) => (
                <ManageUserCard key={u.Id} user={u} service={serviceRef.current} />
              ))}
            </div>
          ) : (
            <div className="bg-[#14141d] border-[1px] border-solid border-[#20202c] rounded-2xl px-[22px] py-[18px] text-[#b6b6c1] text-sm">
              No users yet.
            </div>
          )}

          {/* Legals */}
          <div className="flex items-center gap-[14px] mt-7 pt-2 px-1.5 text-[13px] text-[#8a8a96] font-body-primary">
            <a
              href="https://bestgiftgroup.com/terms-conditions-vendors/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#8a8a96] no-underline hover:text-white hover:underline transition-colors"
            >
              Terms &amp; Conditions
            </a>
            <span className="text-[#8a8a96] opacity-60">|</span>
            <a
              href="https://bestgiftgroup.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#8a8a96] no-underline hover:text-white hover:underline transition-colors"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ProfileForm };

